import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  Input,
  Select,
  Button,
  Tag,
  Avatar,
  Card,
  Row,
  Col,
  Space,
  Modal,
  Checkbox,
  Divider,
  Alert,
  Spin,
} from "antd";
import dayjs from "dayjs";
import {
  SearchOutlined,
  PlusOutlined,
  RightOutlined,
  UserOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ThunderboltOutlined,
  ThunderboltFilled,
  UpOutlined,
  DownOutlined,
  EditOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getApi,
  postApi,
  putApi,
  deleteApi,
} from "../../../../api/api_instance";
import { API_URLS } from "../../../../api/apiUrls";
import PopupCard from "../../../Auth/PopupCard";
import { ReactComponent as DeleteIcon } from "../../../../assets/svgs/delete.svg";
import {
  createSubAdmin,
  fetchProfileData,
} from "../../../../redux/ProfileSlice/profileSlice";
const { Option } = Select;

const DASHBOARD_SUB_MODULES = [
  { label: "Emission Tracking", key: "emission-tracking" },
  { label: "Environmental Impact", key: "environmental-impact" },
  { label: "Social", key: "social" },
  { label: "Governance", key: "governance" },
  { label: "Supply Chain", key: "supply-chain" },
  { label: "SDG", key: "sdg" },
];

const STANDALONE_MODULES = [
  { label: "Project", key: "project" },
  { label: "Report", key: "report" },
  { label: "Standard Form", key: "standard_form" },
  { label: "User Management", key: "usermanagement" },
  { label: "Roles and Permissions", key: "roles-and-permissions" },
  { label: "My Profile", key: "profile" },
  { label: "License & Access", key: "license" },
  { label: "Organization Setup", key: "org-setup" },
  { label: "Support", key: "support" },
];

const PERMISSION_MODULES = [...DASHBOARD_SUB_MODULES, ...STANDALONE_MODULES];

// Modules where Manage is frozen (disabled/unchecked). Full Access only grants View.
const VIEW_ONLY_MODULE_KEYS = new Set([
  "emission-tracking",
  "environmental-impact",
  "social",
  "governance",
  "supply-chain",
  "sdg",
  "license",
  "standard_form",
]);

const AssignSubAdminModal = ({
  open,
  onCancel,
  refresh,
  editData,
  activeTab,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { profileData } = useSelector((state) => state.profile);
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;
  const activeLicense =
    activeTab === "sub-admin"
      ? profile?.license?.sub_admin || {}
      : profile?.license?.management_view || {};

  console.log("user", user);

  const clientId = user?.client_id?._id || user?.clientId || user?._id;

  const [projects, setProjects] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [permissions, setPermissions] = useState(
    PERMISSION_MODULES.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.key]: { view: false, manage: false, fullAccess: false },
      }),
      {},
    ),
  );
  const [isUserListOpen, setIsUserListOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchText, setUserSearchText] = useState("");
  const containerRef = useRef(null);
  const [popupState, setPopupState] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  useEffect(() => {
    if (open && editData) {
      // Set values for edit mode
      if (editData.employeeId) {
        setSelectedUsers([editData.employeeId]);
      }

      // Map project objects to IDs
      const projIds = (editData.scope?.projectIds || []).map((p) =>
        typeof p === "string" ? p : p._id,
      );
      setSelectedProjects(projIds);

      // Map permissions from array to state object
      const newPerms = PERMISSION_MODULES.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.key]: { view: false, manage: false, fullAccess: false },
        }),
        {},
      );

      (editData.permissions || []).forEach((p) => {
        if (newPerms[p.module]) {
          const isWrite = p.access === "write";
          const isRead = p.access === "read" || isWrite;
          newPerms[p.module] = {
            view: isRead,
            manage: isWrite,
            fullAccess: isWrite,
          };
        }
      });
      setPermissions(newPerms);
    } else if (open && !editData) {
      // Reset values for create mode
      setSelectedUsers([]);
      setSelectedProjects([]);
      setPermissions(
        PERMISSION_MODULES.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.key]: { view: false, manage: false, fullAccess: false },
          }),
          {},
        ),
      );
      setUserSearchText("");
    }
  }, [open, editData]);

  useEffect(() => {
    if (open && clientId) {
      fetchProjects();
      fetchUsers("");
    }
  }, [open, clientId]);

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_PROJECTS(1, 100, "-createdAt", "", {}),
      });
      if (response && response.success) {
        const today = dayjs().date();
        // Filter projects where end day is not today (21st as per user requirement)
        const filtered = (response.data || []).filter((item) => {
          const endDay = dayjs(item.endDate).date();
          return endDay !== today;
        });
        setProjects(filtered);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchUsers = async (search = "") => {
    setLoadingUsers(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_ALL_USER_BY_CLIENT_ID(clientId, 1, 50, search),
      });
      if (response && response.success) {
        setAvailableUsers(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const getUserName = (user) =>
    user?.name || user?.employeeId?.name || user?.employee_id || "N/A";

  const getUserEmail = (user) =>
    user?.email || user?.employeeId?.email || user?.email_id || "";

  const filteredAvailableUsers = useMemo(() => {
    const search = userSearchText.trim().toLowerCase();

    if (!search) return availableUsers;

    return availableUsers.filter((user) => {
      const name = String(getUserName(user)).toLowerCase();
      const email = String(getUserEmail(user)).toLowerCase();
      const employeeCode = String(
        user?.employeeId?.employee_id || "",
      ).toLowerCase();
      return (
        name.includes(search) ||
        email.includes(search) ||
        employeeCode.includes(search)
      );
    });
  }, [availableUsers, userSearchText]);

  const handleProjectToggle = (projectId) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter((id) => id !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };

  const licensesPerUser = selectedProjects.length;
  let totalLicensesRequired = 0;
  if (selectedUsers.length > 0) {
    totalLicensesRequired =
      activeTab === "sub-admin"
        ? licensesPerUser <= 1
          ? 1
          : licensesPerUser
        : 1;
  }

  const handlePermissionChange = (moduleKey, type) => {
    setPermissions((prev) => {
      const currentModulePerms = prev[moduleKey] || {
        view: false,
        manage: false,
        fullAccess: false,
      };
      const newModulePerms = { ...currentModulePerms };

      if (type === "view") {
        const nextView = !currentModulePerms.view;
        newModulePerms.view = nextView;
        if (!nextView) {
          newModulePerms.manage = false;
          newModulePerms.fullAccess = false;
        }
      } else if (type === "manage") {
        const nextManage = !currentModulePerms.manage;
        newModulePerms.manage = nextManage;
        if (nextManage) {
          newModulePerms.view = true;
          newModulePerms.fullAccess = true;
        } else {
          newModulePerms.fullAccess = false;
        }
      } else if (type === "fullAccess") {
        const nextFull = !currentModulePerms.fullAccess;
        newModulePerms.fullAccess = nextFull;
        if (VIEW_ONLY_MODULE_KEYS.has(moduleKey)) {
          // For view-only modules: Full Access only controls View; Manage stays frozen
          newModulePerms.view = nextFull;
          newModulePerms.manage = false;
        } else if (nextFull) {
          newModulePerms.view = true;
          newModulePerms.manage = true;
        } else {
          newModulePerms.view = false;
          newModulePerms.manage = false;
        }
      }

      return {
        ...prev,
        [moduleKey]: newModulePerms,
      };
    });
  };

  const getActivePermissions = () => {
    const results = [];
    const dashboardModuleKeys = new Set([
      "emission-tracking",
      "environmental-impact",
      "social",
      "governance",
      "supply-chain",
      "sdg",
    ]);
    const modulesToUse =
      activeTab === "sub-admin"
        ? PERMISSION_MODULES
        : PERMISSION_MODULES.filter((m) =>
            [
              "emission-tracking",
              "environmental-impact",
              "social",
              "governance",
              "supply-chain",
              "sdg",
              "support",
            ].includes(m.key),
          );

    let dashboardPermissionAccess = null;

    modulesToUse.forEach((module) => {
      if (activeTab === "management") {
        if (module.key === "support") {
          results.push({ module: module.key, access: "read" });
        } else if (dashboardModuleKeys.has(module.key)) {
          dashboardPermissionAccess = "read";
        }
        return;
      }

      const p = permissions[module.key];

      // For modules that are view-only (like standard_form), always send read access
      if (VIEW_ONLY_MODULE_KEYS.has(module.key)) {
        if (p?.view || p?.fullAccess) {
          const access = "read";
          results.push({ module: module.key, access });
          if (dashboardModuleKeys.has(module.key)) {
            dashboardPermissionAccess =
              dashboardPermissionAccess === "write" ? "write" : "read";
          }
        }
        return;
      }

      if (p?.manage || p?.fullAccess || p?.edit || p?.delete) {
        const access = "write";
        results.push({ module: module.key, access });
        if (dashboardModuleKeys.has(module.key)) {
          dashboardPermissionAccess = access;
        }
      } else if (p?.view) {
        const access = "read";
        results.push({ module: module.key, access });
        if (dashboardModuleKeys.has(module.key)) {
          dashboardPermissionAccess =
            dashboardPermissionAccess === "write" ? "write" : "read";
        }
      }
    });

    if (
      dashboardPermissionAccess &&
      [...dashboardModuleKeys].some((key) => {
        const p = permissions[key];
        return p?.view || p?.manage || p?.fullAccess || p?.edit || p?.delete;
      })
    ) {
      results.push({ module: "dashboard", access: dashboardPermissionAccess });
    }

    // If user-management module is selected, always include employee module with write access
    const hasUserManagement = results.some(
      (r) => r.module === "usermanagement",
    );
    if (hasUserManagement) {
      const empIdx = results.findIndex((r) => r.module === "employee");
      if (empIdx === -1) {
        results.push({ module: "employee", access: "write" });
      } else {
        // upgrade to write if present with lower access
        results[empIdx].access = "write";
      }
    }

    return results;
  };

  const showPopup = (type, title, popupMessage) => {
    setPopupState({
      open: true,
      type,
      title,
      message: popupMessage,
    });
  };

  const closePopup = () => {
    setPopupState((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (popupState.open) {
      const timer = setTimeout(() => {
        closePopup();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [popupState.open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsUserListOpen(false);
      }
    };

    if (isUserListOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserListOpen]);

  const handleSave = async () => {
    if (selectedUsers.length === 0) {
      showPopup(
        "error",
        "Validation Error",
        "Please select at least one user to assign permissions.",
      );
      return;
    }

    setLoadingUsers(true);
    try {
      if (editData) {
        // Edit mode (PUT)
        const payload = {
          projectIds: selectedProjects,
          permissions: getActivePermissions(),
        };
        const res = await putApi({
          apiUrl: API_URLS.UPDATE_SUB_ADMIN(
            editData.employeeId?._id || editData._id,
          ),
          data: payload,
        });
        if (res && res.success) {
          showPopup("success", "Success", res?.message);
          onCancel();
          if (refresh) refresh();
          if (user?._id) {
            dispatch(fetchProfileData(user._id));
          }
        } else {
          showPopup(
            "error",
            "Error",
            res?.message || "Failed to update sub-admin",
          );
        }
      } else {
        // Create mode (POST)
        const promises = selectedUsers.map((u) => {
          if (activeTab === "sub-admin") {
            const payload = {
              employeeId: u._id,
              projectScope: selectedProjects,
              permissions: getActivePermissions(),
            };
            return dispatch(createSubAdmin(payload)).unwrap();
          } else {
            const payload = {
              employeeId: u._id,
            };
            return postApi({
              apiUrl: API_URLS.CREATE_MANAGEMENT_VIEW,
              data: payload,
            });
          }
        });

        const results = await Promise.all(promises);
        const allSuccessful = results.every((res) => res && res.success);

        if (allSuccessful) {
          showPopup(
            "success",
            "Success",
            `Successfully assigned ${selectedUsers.length} ${activeTab === "sub-admin" ? "sub-admin" : "management view access"}`,
          );
          onCancel();
          if (refresh) refresh();
          if (user?._id) {
            dispatch(fetchProfileData(user._id));
          }
        } else {
          const failedRes = results.find((r) => r && !r.success);
          showPopup(
            "error",
            "Error",
            failedRes?.message ||
              `Some ${activeTab === "sub-admin" ? "sub-admins" : "management view users"} could not be assigned`,
          );
        }
      }
    } catch (error) {
      console.error("Save Error:", error);
      showPopup(
        "error",
        "Error",
        typeof error === "string"
          ? error
          : error?.response?.data?.message ||
              error?.message ||
              "Failed to assign sub-admins",
      );
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUsers([user]);
    setUserSearchText("");
    setIsUserListOpen(false);
  };

  const handleRemoveUser = (email) => {
    setSelectedUsers(selectedUsers.filter((u) => getUserEmail(u) !== email));
  };

  return (
    <>
      <Modal
        title={
          <div className="pt-4 px-2">
            <h2 className="text-xl font-bold text-black mb-0">
              {activeTab === "sub-admin"
                ? "Assign Sub-admin"
                : "Assign Management View User"}
            </h2>
            <p className="text-sm font-normal text-black mt-1">
              {activeTab === "sub-admin"
                ? "Set up project or account-level access"
                : "Grant dashboard-wide read-only access"}
            </p>
          </div>
        }
        open={open}
        onCancel={onCancel}
        footer={null}
        width={700}
        className="assign-subadmin-modal"
        closeIcon={<CloseOutlined className="text-black hover:text-black" />}
      >
        <div className="py-2 px-2 font-lexend">
          {/* Select User Section */}
          <div className="mb-6 relative">
            <label className="block text-sm font-bold text-black mb-2 font-lexend">
              Select User
            </label>

            <div className="relative" ref={containerRef}>
              <Input
                placeholder="Search by name or email..."
                className={`h-11 rounded-lg border-gray-200 focus:border-[#06513D] focus:ring-1 focus:ring-[#06513D]/20 transition-all ${isUserListOpen ? "rounded-b-none border-b-0" : ""}`}
                onClick={() => setIsUserListOpen(true)}
                value={userSearchText}
                onChange={(e) => {
                  setUserSearchText(e.target.value);
                  setIsUserListOpen(true);
                }}
              />

              {/* Dropdown User List */}
              {isUserListOpen && (
                <div className="absolute top-full left-0 w-full bg-white border border-gray-200 border-t-0 rounded-b-lg shadow-lg z-50 overflow-hidden">
                  <div className="max-h-56 overflow-y-auto custom-scrollbar">
                    {loadingUsers ? (
                      <div className="p-4 text-center">
                        <Spin size="small" />
                      </div>
                    ) : (
                      filteredAvailableUsers
                        .filter(
                          (u) =>
                            !selectedUsers.find(
                              (su) => getUserEmail(su) === getUserEmail(u),
                            ),
                        )
                        .map((user, idx) => (
                          <div
                            key={idx}
                            className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 group ${editData ? "pointer-events-none opacity-50" : ""}`}
                            onClick={() => !editData && handleUserSelect(user)}
                          >
                            <div className="text-sm font-bold text-black group-hover:text-[#06513D] transition-colors">
                              {getUserName(user)}
                            </div>
                            <div className="text-xs text-black font-normal">
                              {getUserEmail(user)}
                            </div>
                          </div>
                        ))
                    )}
                    {!loadingUsers && filteredAvailableUsers.length === 0 && (
                      <div className="p-4 text-center text-black text-xs">
                        No users found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {selectedUsers.length > 0 && (
              <div className="mt-4 flex flex-col gap-2 animate-fadeIn">
                {selectedUsers.map((user, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-[#E6EEEC] border border-[#06513D] rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="bg-[#06513D] text-[#fffff] font-bold text-xs ring-1 ring-[#06513D]">
                        {user.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-black leading-tight">
                          {getUserName(user)}
                        </span>
                        <span className="text-xs text-black">
                          {getUserEmail(user)}
                        </span>
                      </div>
                    </div>
                    {!editData && (
                      <CloseOutlined
                        className="text-[#06513D] cursor-pointer hover:text-red-500 transition-colors p-1"
                        onClick={() => handleRemoveUser(getUserEmail(user))}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {activeTab === "sub-admin" && (
            <div className="mb-6">
              <div className="flex justify-between items-start mb-1">
                <div>
                  <label className="block text-sm font-bold text-black font-lexend">
                    Project Access
                  </label>
                  <p className="text-xs text-black">
                    Required OR Account Access
                  </p>
                </div>
                <Tag
                  color="#06513D"
                  className="flex items-center gap-1 border-[#06513D] text-[#06513D] rounded px-2 py-0.5 m-0 font-medium text-[11px]"
                >
                  <ThunderboltFilled className="text-[10px]" /> Per-Project
                </Tag>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden relative">
                <div className="max-h-36 overflow-y-auto py-1 scrollbar-hide">
                  {loadingProjects ? (
                    <div className="p-4 text-center">
                      <Spin />
                    </div>
                  ) : projects.length > 0 ? (
                    projects.map((project, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${selectedProjects.includes(project._id) ? "bg-green-50/30" : ""}`}
                        onClick={() => handleProjectToggle(project._id)}
                      >
                        <Checkbox
                          checked={selectedProjects.includes(project._id)}
                          className="mr-3 custom-checkbox"
                        />
                        <span
                          className={`text-sm ${selectedProjects.includes(project._id) ? "text-[#06513D] font-medium" : "text-black"}`}
                        >
                          {project.name}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-black text-xs">
                      No active projects available
                    </div>
                  )}
                </div>
                <div className="absolute right-3 top-2 flex flex-col gap-1 pointer-events-none opacity-40">
                  <UpOutlined className="text-[10px]" />
                  <DownOutlined className="text-[10px]" />
                </div>
              </div>
            </div>
          )}

          {/* Granular Permissions Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-[15px] font-bold text-black font-lexend m-0">
                Permissions
              </h3>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white">
              {/* Table Header */}
              <div className="bg-[#E6EEEC] px-4 py-2.5 flex items-center justify-between border-b border-gray-200">
                <span className="text-[13px] font-bold text-[#06513D] font-lexend uppercase tracking-wider">
                  Module Name
                </span>
                <div className="flex gap-10 sm:gap-14 pr-4">
                  <span className="text-[12px] font-bold text-[#06513D] w-12 text-center uppercase tracking-wider">
                    View
                  </span>
                  <span className="text-[12px] font-bold text-[#06513D] w-14 text-center uppercase tracking-wider">
                    Manage
                  </span>
                  <span className="text-[12px] font-bold text-[#06513D] w-16 text-center uppercase tracking-wider">
                    Full Access
                  </span>
                </div>
              </div>

              <div className="divide-y divide-gray-100 max-h-[360px] overflow-y-auto custom-scrollbar">
                {/* 1. Dashboard Heading (No Checkbox) */}
                <div className="bg-gray-50/80 px-4 py-2.5 flex items-center border-b border-gray-200">
                  <span className="text-[14px] font-extrabold text-[#06513D] font-lexend uppercase tracking-wide">
                    Dashboard
                  </span>
                </div>

                {/* Dashboard Sub-Modules — Manage is always frozen */}
                {DASHBOARD_SUB_MODULES.map((module) => (
                  <div
                    key={module.key}
                    className="flex items-center justify-between py-2.5 px-4 hover:bg-gray-50/60 transition-colors pl-8"
                  >
                    <span className="text-[14px] font-bold text-black">
                      {module.label}
                    </span>
                    <div className="flex gap-10 sm:gap-14 pr-4">
                      <div className="w-12 flex justify-center">
                        <Checkbox
                          checked={
                            activeTab === "management"
                              ? true
                              : permissions[module.key]?.view
                          }
                          disabled={activeTab === "management"}
                          onChange={() =>
                            activeTab === "sub-admin" &&
                            handlePermissionChange(module.key, "view")
                          }
                          className="custom-checkbox"
                        />
                      </div>
                      {/* Manage — permanently frozen for dashboard modules */}
                      <div className="w-14 flex justify-center">
                        <Checkbox
                          checked={false}
                          disabled={true}
                          className="custom-checkbox opacity-40 cursor-not-allowed"
                        />
                      </div>
                      <div className="w-16 flex justify-center">
                        <Checkbox
                          checked={
                            activeTab === "management"
                              ? false
                              : permissions[module.key]?.fullAccess
                          }
                          disabled={activeTab === "management"}
                          onChange={() =>
                            activeTab === "sub-admin" &&
                            handlePermissionChange(module.key, "fullAccess")
                          }
                          className="custom-checkbox"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* 2. Standalone Modules */}
                {STANDALONE_MODULES.filter((m) =>
                  activeTab === "sub-admin" ? true : m.key === "support",
                ).map((module) => {
                  const isViewOnly = VIEW_ONLY_MODULE_KEYS.has(module.key);
                  return (
                    <div
                      key={module.key}
                      className="flex items-center justify-between py-2.5 px-4 hover:bg-gray-50/60 transition-colors"
                    >
                      <span className="text-[14px] font-bold text-black">
                        {module.label}
                      </span>
                      <div className="flex gap-10 sm:gap-14 pr-4">
                        <div className="w-12 flex justify-center">
                          <Checkbox
                            checked={
                              activeTab === "management"
                                ? true
                                : permissions[module.key]?.view
                            }
                            disabled={activeTab === "management"}
                            onChange={() =>
                              activeTab === "sub-admin" &&
                              handlePermissionChange(module.key, "view")
                            }
                            className="custom-checkbox"
                          />
                        </div>
                        {/* Manage — frozen for view-only modules */}
                        <div className="w-14 flex justify-center">
                          {isViewOnly ? (
                            <Checkbox
                              checked={false}
                              disabled={true}
                              className="custom-checkbox opacity-40 cursor-not-allowed"
                            />
                          ) : (
                            <Checkbox
                              checked={
                                activeTab === "management"
                                  ? false
                                  : permissions[module.key]?.manage
                              }
                              disabled={activeTab === "management"}
                              onChange={() =>
                                activeTab === "sub-admin" &&
                                handlePermissionChange(module.key, "manage")
                              }
                              className="custom-checkbox"
                            />
                          )}
                        </div>
                        <div className="w-16 flex justify-center">
                          <Checkbox
                            checked={
                              activeTab === "management"
                                ? false
                                : permissions[module.key]?.fullAccess
                            }
                            disabled={activeTab === "management"}
                            onChange={() =>
                              activeTab === "sub-admin" &&
                              handlePermissionChange(module.key, "fullAccess")
                            }
                            className="custom-checkbox"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mb-4 p-4 rounded-xl border border-[#06513D] bg-[#E6EEEC]">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-[#06513D] rounded-lg flex items-center justify-center">
                <ThunderboltFilled className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-base font-bold text-black mb-0 leading-tight">
                  {totalLicensesRequired}{" "}
                  {activeTab === "sub-admin" ? "Licenses" : "License"} Required
                </h3>
                <p className="text-xs text-black mt-1">
                  {activeTab === "sub-admin"
                    ? totalLicensesRequired > 0
                      ? "Allocating licenses based on selected projects"
                      : "Select projects to allocate licenses"
                    : "Assigning 1 Management View license to the selected user"}
                </p>
              </div>
            </div>
            <Divider className="my-3 border-[#06513D]/50" />
            <div className="flex items-center text-[11px] font-bold text-[#06513D] uppercase tracking-wide">
              Available: {activeLicense.unused ?? 0} /{" "}
              {activeLicense.total ?? 0} licenses
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2 pb-1 border-t border-gray-50 mt-4">
            <Button
              className="h-10 px-8 rounded-lg border-gray-300 hover:!bg-[#F8F8F8] hover:!text-[#06513D] hover:!border-[#06513D] bg-[#E6EDEB] text-[#06513D] font-bold "
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="h-10 px-8 rounded-lg font-bold border-none hover:!bg-[#387464] bg-[#06513D]"
              loading={loadingUsers}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>

        <style jsx>{`
          .assign-subadmin-modal :global(.ant-modal-content) {
            border-radius: 16px !important;
            padding: 12px 20px !important;
          }
          .assign-subadmin-modal :global(.ant-modal-header) {
            border-bottom: none !important;
            margin-bottom: 8px !important;
          }
          :global(.assign-subadmin-modal .custom-checkbox .ant-checkbox-inner) {
            border-radius: 4px !important;
          }
          :global(
            .assign-subadmin-modal
              .custom-checkbox
              .ant-checkbox-checked
              .ant-checkbox-inner
          ) {
            background-color: #06513d !important;
            border-color: #06513d !important;
          }
          :global(
            .assign-subadmin-modal .custom-checkbox .ant-checkbox-checked::after
          ) {
            border-color: #06513d !important;
          }
          :global(
            .assign-subadmin-modal .custom-checkbox:hover .ant-checkbox-inner
          ),
          :global(
            .assign-subadmin-modal
              .custom-checkbox:hover
              .ant-checkbox-checked
              .ant-checkbox-inner
          ) {
            border-color: #06513d !important;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 10px;
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </Modal>

      {popupState.open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 px-4">
          <PopupCard
            type={popupState.type}
            title={popupState.title}
            message={popupState.message}
          />
        </div>
      )}
    </>
  );
};

const RolesPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { profileData } = useSelector((state) => state.profile);
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;
  const showSubAdminManagement = !user?.subAdmin?.isSubAdmin;
  const [activeTab, setActiveTab] = useState(
    showSubAdminManagement ? "sub-admin" : "management",
  );
  const activeLicense =
    activeTab === "sub-admin"
      ? profile?.license?.sub_admin || {}
      : profile?.license?.management_view || {};

  const [searchTerm, setSearchTerm] = useState("");
  const [roleType, setRoleType] = useState("All");
  const [status, setStatus] = useState("All");
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    visible: false,
    record: null,
  });
  const [successPopup, setSuccessPopup] = useState({
    visible: false,
    title: "",
    message: "",
  });

  useEffect(() => {
    if (user?._id) {
      dispatch(fetchProfileData(user._id));
    }
  }, [dispatch, user?._id]);

  useEffect(() => {
    if (!showSubAdminManagement) {
      setActiveTab("management");
      return;
    }
    fetchSubAdmins(activeTab);
  }, [activeTab, showSubAdminManagement]);

  const fetchSubAdmins = async (tab = activeTab) => {
    setLoading(true);
    try {
      const url =
        tab === "sub-admin"
          ? API_URLS.GET_SUB_ADMINS
          : API_URLS.GET_MANAGEMENT_VIEWS;
      const response = await getApi({ apiUrl: url });
      if (response && response.success) {
        setData(response.data || []);
      }
    } catch (error) {
      console.error(`Error fetching ${tab}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditData(record);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const getUserName = (record) =>
    record?.employeeId?.name || record?.name || "N/A";

  const getUserEmail = (record) =>
    record?.employeeId?.email || record?.email || "";

  const getProjectNames = (record) =>
    (record?.scope?.projectIds || [])
      .map((project) => project?.name || "")
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const filteredData = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return data.filter((record) => {
      const matchesStatus =
        status === "All" ||
        String(record?.employeeId?.status || record?.status || "")
          .toLowerCase()
          .includes(status.toLowerCase());

      if (!matchesStatus) return false;

      if (!search) return true;

      const name = getUserName(record).toLowerCase();
      const email = getUserEmail(record).toLowerCase();
      const employeeCode = String(
        record?.employeeId?.employee_id || record?.employee_id || "",
      ).toLowerCase();
      const projects = getProjectNames(record);

      return (
        name.includes(search) ||
        email.includes(search) ||
        employeeCode.includes(search) ||
        projects.includes(search)
      );
    });
  }, [data, searchTerm, status]);

  const columns = [
    {
      title: "NAME",
      dataIndex: ["employeeId", "name"],
      key: "name",
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <Avatar className="bg-green-50 text-[#06513D] font-bold text-xs ring-1 ring-green-100">
            {record.employeeId?.name
              ? record.employeeId.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
              : "U"}
          </Avatar>
          <span className="font-semibold text-black">
            {record.employeeId?.name || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "EMAIL",
      dataIndex: ["employeeId", "email"],
      key: "email",
      render: (text, record) => (
        <span className="text-black">{record.employeeId?.email || "N/A"}</span>
      ),
    },
    {
      title: "PROJECTS",
      dataIndex: ["scope", "projectIds"],
      key: "projects",
      render: (_, record) => {
        if (activeTab === "management") {
          return <div className="text-black">All Projects</div>;
        }
        const projectNames = (record.scope?.projectIds || [])
          .map((project) => project?.name)
          .filter(Boolean);

        return (
          <div className="text-black max-w-[320px] whitespace-normal break-words">
            {projectNames.length > 0 ? projectNames.join(", ") : "N/A"}
          </div>
        );
      },
    },
    {
      title: "ACCOUNT ACCESS",
      key: "accountAccess",
      render: (_, record) => {
        if (activeTab === "management") {
          return (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium border bg-green-50 text-green-700 border-green-100">
              <CheckOutlined className="text-[10px]" />
              Yes
            </div>
          );
        }
        const hasAccountAccess = (record.permissions || []).length > 0;
        return (
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium border ${
              hasAccountAccess
                ? "bg-green-50 text-green-700 border-green-100"
                : "bg-green-50 text-[#06513D] border-green-100"
            }`}
          >
            {hasAccountAccess ? (
              <CheckOutlined className="text-[10px]" />
            ) : (
              <CloseOutlined className="text-[10px]" />
            )}
            {hasAccountAccess ? "Yes" : "No"}
          </div>
        );
      },
    },
    {
      title: "LICENSES USED",
      key: "licensesUsed",
      render: (_, record) => {
        if (activeTab === "management") {
          return (
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 bg-green-50 text-[#06513D] rounded border border-green-100">
                ✧
              </div>
              <span className="font-medium text-black">1</span>
            </div>
          );
        }
        const projectCount = record.scope?.projectIds?.length || 0;
        const count = projectCount > 1 ? projectCount : 1;
        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 bg-green-50 text-[#06513D] rounded border border-green-100">
              ✧
            </div>
            <span className="font-medium text-black">{count}</span>
          </div>
        );
      },
    },
    {
      title: "STATUS",
      dataIndex: ["employeeId", "status"],
      key: "status",
      render: (status) => (
        <div
          className={`inline-block px-3 py-0.5 rounded-full text-[11px] font-bold ${
            status === "Active"
              ? "bg-green-50 text-green-700"
              : "bg-red-50 text-red-700"
          }`}
        >
          {status || "Unknown"}
        </div>
      ),
    },
    {
      title: "ACTION",
      key: "action",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {activeTab === "sub-admin" && (
            <EditOutlined
              className="text-[#06513D] cursor-pointer hover:text-[#06513D] transition-colors"
              onClick={() => handleEdit(record)}
            />
          )}

          <DeleteIcon
            style={{ cursor: "cursor-pointer" }}
            onClick={() => {
              setDeleteConfirmation({
                visible: true,
                record: record,
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="p-6  min-h-screen font-lexend">
      {/* Tab Switcher */}
      <div className="flex gap-4 mb-8 border-b border-gray-100 pb-4 relative">
        {showSubAdminManagement && (
          <button
            onClick={() => setActiveTab("sub-admin")}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all text-sm relative ${
              activeTab === "sub-admin"
                ? "bg-[#06513D] text-white shadow-sm"
                : "bg-white text-black border border-gray-200 hover:text-black hover:bg-gray-50"
            }`}
          >
            <UserOutlined />
            Sub-Admin Management
            {activeTab === "sub-admin" && (
              <div className="absolute left-0 right-0 bottom-[-17px] h-[3px] bg-[#06513D]" />
            )}
          </button>
        )}
        <button
          onClick={() => setActiveTab("management")}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all text-sm relative ${
            activeTab === "management"
              ? "bg-[#06513D] text-white shadow-sm"
              : "bg-white text-black border border-gray-200 hover:text-black hover:bg-gray-50"
          }`}
        >
          <CrownOutlined />
          Management View
          {activeTab === "management" && (
            <div className="absolute left-0 right-0 bottom-[-17px] h-[3px] bg-[#06513D]" />
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            title: "Total Licenses",
            value: activeLicense.total ?? "0",
            icon: <UserOutlined />,
            color: "blue",
          },
          {
            title: "Used Licenses",
            value: activeLicense.used ?? "0",
            icon: <KeyOutlined />,
            color: "emerald",
          },
          {
            title: "Available",
            value: activeLicense.unused ?? "0",
            icon: <CheckCircleOutlined />,
            color: "green",
          },
          {
            title: "Status",
            value: profile?.status || "Active",
            icon: <CheckCircleOutlined />,
            color: "green",
          },
        ].map((card, i) => (
          <div
            key={i}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col items-center"
          >
            <div
              className={`w-12 h-12 rounded-lg mb-3 flex items-center justify-center bg-${card.color}-50 text-${card.color}-600`}
            >
              {React.cloneElement(card.icon, { className: "text-2xl" })}
            </div>
            <div className="text-3xl font-extrabold text-black">
              {card.value}
            </div>
            <div className="text-xs text-black mt-2 uppercase tracking-wide font-medium">
              {card.title}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 mt-8 mb-6">
        <div className="flex flex-col lg:flex-row items-end gap-4">
          <div className="flex-1 w-full">
            <Input
              placeholder="Search by name or email..."
              prefix={<SearchOutlined className="text-black" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-[42px] rounded-lg border-gray-200"
            />
          </div>

          <div className="w-full lg:w-48">
            <label className="text-xs text-black mb-1 block">Status</label>
            <Select
              value={status}
              onChange={setStatus}
              className="w-full h-[42px] [&>.ant-select-selector]:rounded-lg"
            >
              <Option value="All">All</Option>
              <Option value="Active">Active</Option>
              <Option value="Revoked">Revoked</Option>
            </Select>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="h-[42px] px-6 rounded-lg bg-[#06513D] hover:!bg-[#387464] focus:!bg-[#387464] active:!bg-[#06513D] border-none font-bold whitespace-nowrap add-btn-green"
            onClick={handleAddNew}
          >
            {activeTab === "sub-admin"
              ? "Add sub-admin"
              : "Add management view user"}
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={false}
        loading={loading}
        rowKey="_id"
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden [&_.ant-table-thead_th]:bg-gray-50/50 [&_.ant-table-thead_th]:text-black [&_.ant-table-thead_th]:text-[11px] [&_.ant-table-thead_th]:font-bold [&_.ant-table-thead_th]:uppercase [&_.ant-table-thead_th]:tracking-wider [&_.ant-table-thead_th]:border-b-gray-100"
        rowClassName={() =>
          "hover:bg-gray-50/50 transition-colors cursor-pointer"
        }
      />

      <AssignSubAdminModal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditData(null);
        }}
        refresh={() => fetchSubAdmins(activeTab)}
        editData={editData}
        activeTab={activeTab}
      />

      {/*------------- DELETE SUB ADMIN------- */}
      {deleteConfirmation.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="warning"
            title="Confirm Delete"
            message={`Are you sure you want to delete?`}
            showActions={true}
            confirmText="Yes, Delete"
            cancelText="Cancel"
            onCancel={() =>
              setDeleteConfirmation({
                visible: false,
                record: null,
              })
            }
            onConfirm={async () => {
              try {
                const record = deleteConfirmation.record;
                if (record) {
                  const url =
                    activeTab === "sub-admin"
                      ? API_URLS.DELETE_SUB_ADMIN(
                          record.employeeId?._id || record._id,
                        )
                      : API_URLS.DELETE_MANAGEMENT_VIEW(
                          record.employeeId?._id || record._id,
                        );
                  const res = await deleteApi({
                    apiUrl: url,
                  });
                  if (res && res.success) {
                    setDeleteConfirmation({
                      visible: false,
                      record: null,
                    });

                    setSuccessPopup({
                      visible: true,
                      title: "Deleted",
                      message: `${record.employeeId?.name || (activeTab === "sub-admin" ? "Sub-admin" : "Management View user")} has been deleted successfully!`,
                    });

                    fetchSubAdmins(activeTab);
                    if (user?._id) {
                      dispatch(fetchProfileData(user._id));
                    }

                    setTimeout(() => {
                      setSuccessPopup({
                        visible: false,
                        title: "",
                        message: "",
                      });
                    }, 3000);
                  } else {
                    console.error("Delete failed:", res?.message);
                  }
                }
              } catch (err) {
                console.error("Failed to delete user:", err);
              }
            }}
          />
        </div>
      )}
      {successPopup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type="success"
            title={successPopup.title}
            message={successPopup.message}
          />
        </div>
      )}
      <style jsx>{`
        :global(.add-btn-green) {
          background-color: #06513d !important;
          border-color: #06513d !important;
          color: #fff !important;
        }
        :global(.add-btn-green:hover),
        :global(.add-btn-green:focus),
        :global(.add-btn-green:active) {
          background-color: #044a38 !important;
          border-color: #044a38 !important;
          color: #fff !important;
        }
      `}</style>
    </div>
  );
};

export default RolesPage;
